import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, ArrowLeft } from "lucide-react";

export const GiftCardPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="gift-card-policy-page" className="pt-20">
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/gift-cards" className="inline-flex items-center gap-2 text-[#C5A059] text-sm hover:underline mb-8">
            <ArrowLeft size={14} /> Back to Gift Cards
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-6">
              <Gift className="w-8 h-8 text-[#C5A059]" />
              <h1 className="font-heading text-3xl sm:text-4xl text-white">Gift Card Policy</h1>
            </div>

            <div className="bg-[#121212] border border-[#262626] p-8 md:p-10 space-y-8">

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  1. General Terms
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Luxury Beauty digital gift cards are redeemable for any service, treatment, or training course offered by Luxury Beauty at our Georgetown and Mahaica locations.</p>
                  <p>Gift cards are issued in Guyanese Dollars (GYD) and are non-transferable for cash value.</p>
                  <p>Each gift card is assigned a unique code upon purchase and must be presented at the time of redemption.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  2. Purchase & Payment
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Gift cards can be purchased in any amount starting from $1,000 GYD through our website.</p>
                  <p>Upon completing the online purchase form, a Luxury Beauty team member will contact you to arrange payment.</p>
                  <p>The gift card code will only be activated once full payment has been received and confirmed.</p>
                  <p>Accepted payment methods will be communicated at the time of purchase confirmation.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  3. Redemption
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Gift cards may be redeemed for any Luxury Beauty service, treatment, or training course.</p>
                  <p>The gift card code must be presented or quoted at the time of booking or payment.</p>
                  <p>If the total cost of services exceeds the gift card balance, the remaining amount must be paid by the client.</p>
                  <p>If the total cost is less than the gift card value, the remaining balance will stay on the card for future use.</p>
                  <p>Gift cards can be used across multiple visits until the balance is fully redeemed.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  4. Validity & Expiration
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Gift cards are valid for twelve (12) months from the date of purchase.</p>
                  <p>Expired gift cards cannot be redeemed or refunded. It is the responsibility of the holder to use the card within the validity period.</p>
                  <p>Luxury Beauty reserves the right to extend validity periods at its sole discretion.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  5. Refund & Exchange Policy
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Gift cards are <strong className="text-white">non-refundable</strong> and cannot be exchanged for cash.</p>
                  <p>Lost or stolen gift cards cannot be replaced. Please treat your gift card code as you would cash.</p>
                  <p>Luxury Beauty is not responsible for unauthorized use of gift card codes.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  6. Limitations
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>Gift cards cannot be used to purchase other gift cards.</p>
                  <p>Gift cards have no cash value and cannot be sold or auctioned by the holder.</p>
                  <p>Luxury Beauty reserves the right to refuse redemption if fraud is suspected.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  7. Balance Inquiries
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>You can check your gift card balance by contacting us at <strong className="text-white">+592 647 1133</strong> or visiting our location.</p>
                  <p>Please have your gift card code ready when inquiring about your balance.</p>
                </div>
              </div>

              <div>
                <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  8. Contact
                </h2>
                <div className="space-y-2 font-body text-sm text-[#A3A3A3] leading-relaxed">
                  <p>For questions regarding gift cards, please contact us:</p>
                  <p>Phone: <strong className="text-white">+592 647 1133</strong></p>
                  <p>Address: Lot 18 Helena #1, Mahaica, East Coast Demerara, Guyana</p>
                </div>
              </div>

              <div className="pt-6 border-t border-[#262626]">
                <p className="font-body text-xs text-[#A3A3A3]">
                  This policy is effective as of January 2026. Luxury Beauty reserves the right to modify this policy at any time without prior notice. Any changes will be posted on our website.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/gift-cards"
                className="inline-flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all"
              >
                <Gift size={16} /> Shop Gift Cards
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GiftCardPolicyPage;
